/**
 * T1252 · Devtool channel · cache:write with source classification.
 *
 * Spec 05 §L2828. Subscribes to `QueryCache.subscribe('updated')` and
 * emits `cache:write` with the source of the write.
 *
 * **Sources** (per file 05 §L2829):
 *
 *   - `rest`       — direct setQueryData from fetchSpec REST path (default).
 *   - `nats`       — NATS-routed setQueryData (routing dispatcher).
 *   - `optimistic` — useSdkMutation onMutate optimistic write.
 *   - `broadcast`  — sibling-tab BroadcastChannel apply.
 *   - `ssr`        — Next.js hydration / Suspense boundary.
 *   - `snapshot`   — replayed from persister hydrate.
 *
 * **Per-write marker.** Source attribution requires the writer to mark
 * the NEXT setQueryData via `markNextWrite(qc, key, source)`. The marker
 * is consumed once — a subsequent write without re-marking defaults to
 * `'rest'`. Markers are stored in a per-QueryClient `WeakMap<keyHash,
 * source>` so replacing the QueryClient does not leak.
 */

import { emit, isDevtoolEnabled } from '../events.js';
import { _bumpPerf } from '../perf-counters.js';

export type CacheWriteSource =
  | 'projection'
  | 'rest'
  | 'nats'
  | 'optimistic'
  | 'broadcast'
  | 'ssr'
  | 'snapshot'
  | 'debug';

export interface CacheWriteEvent {
  readonly queryHash: string;
  readonly queryKey: readonly unknown[];
  readonly source: CacheWriteSource;
  readonly dataUpdatedAt: number;
}

export interface QueryLike {
  readonly queryHash: string;
  readonly queryKey: readonly unknown[];
  readonly state: { readonly dataUpdatedAt: number };
}

export interface QueryCacheEventLike {
  readonly type: string;
  readonly action?: { readonly type?: string };
  readonly query: QueryLike;
}

export interface QueryCacheLike {
  subscribe(cb: (e: QueryCacheEventLike) => void): () => void;
}

export interface QueryClientLike {
  getQueryCache(): QueryCacheLike;
}

// One marker map per QueryClient. WeakMap prevents leaks when a
// QueryClient is replaced (tests, route remounts) — the GC reclaims
// the inner Map alongside the dead client.
const markers = new WeakMap<QueryClientLike, Map<string, CacheWriteSource>>();

function getMarkerMap(qc: QueryClientLike): Map<string, CacheWriteSource> {
  let m = markers.get(qc);
  if (m === undefined) {
    m = new Map<string, CacheWriteSource>();
    markers.set(qc, m);
  }
  return m;
}

/**
 * Mark the NEXT cache write against `queryHash` as carrying `source`.
 * One-shot: consumed by the next emit, then cleared. If never
 * consumed, the marker lingers until the QueryClient is GC'd (WeakMap
 * leak guard).
 */
export function markNextWrite(
  qc: QueryClientLike,
  queryHash: string,
  source: CacheWriteSource,
): void {
  getMarkerMap(qc).set(queryHash, source);
  // Perf counter bump — `markNextWrite` is itself only called from
  // build-time-gated branches (see useRealtimeQuery / createMutation),
  // so this bump piggybacks on the same DCE strip. We import lazily
  // via dynamic require to avoid a circular import between
  // `channels/cache-write` and `perf-counters` if either grows shared
  // helpers later; the import resolves once at module load and the
  // call cost is one indirect call + Map lookup.
  if (process.env.NODE_ENV !== 'production') {
    const key =
      source === 'projection'
        ? 'cacheWrites.byProjection'
        : source === 'nats'
          ? 'cacheWrites.byNats'
          : source === 'optimistic'
            ? 'cacheWrites.byOptimistic'
            : source === 'ssr'
              ? 'cacheWrites.bySsr'
              : source === 'snapshot'
                ? 'cacheWrites.bySnapshot'
                : source === 'broadcast'
                  ? 'cacheWrites.byBroadcast'
                  : source === 'debug'
                    ? 'cacheWrites.byDebug'
                    : 'cacheWrites.byRest';
    _bumpPerf(key);
  }
}

/**
 * Inspect-only: returns the pending marker without consuming it.
 * Useful in tests to assert producer ordering.
 */
export function peekNextWrite(
  qc: QueryClientLike,
  queryHash: string,
): CacheWriteSource | undefined {
  return markers.get(qc)?.get(queryHash);
}

/**
 * Install the cache:write channel against a `QueryClient`. The
 * returned closure detaches the subscriber (idempotent).
 */
export function installCacheWriteChannel(qc: QueryClientLike): () => void {
  if (!isDevtoolEnabled()) {
    return (): void => {};
  }
  const cache = qc.getQueryCache();
  return cache.subscribe((event: QueryCacheEventLike) => {
    if (event.type !== 'updated') return;
    // Only data-bearing actions count as a "write". Action types
    // from TanStack: 'success' | 'error' | 'invalidate' | 'failed'
    // | 'pause' | 'continue' | 'setState' | 'fetch'.
    const actionType = event.action?.type;
    if (actionType !== 'success' && actionType !== 'setState' && actionType !== undefined) {
      return;
    }
    const map = markers.get(qc);
    const marked = map?.get(event.query.queryHash);
    // Consume the marker (one-shot).
    map?.delete(event.query.queryHash);
    const source: CacheWriteSource = marked ?? 'rest';
    const payload: CacheWriteEvent = {
      queryHash: event.query.queryHash,
      queryKey: event.query.queryKey,
      source,
      dataUpdatedAt: event.query.state.dataUpdatedAt,
    };
    emit('cache:write', payload);
  });
}
